import {NetworkState, rootState} from '@mintdata-wsp/common/state/RootState';
import {ImportId} from '@mintdata-wsp/common/state/ui/visualDesigner/ImportState';
import {UploadInstance} from '@mintdata-wsp/common/state/ui/visualDesigner/SurfaceFileUploadState';
import {
    ErrorToastData,
    FileUploadToastData,
    SlowConnectionToastData,
    SocketReconnectionToastData,
    ToastData,
    ToastId
} from '@mintdata-wsp/common/state/ui/visualDesigner/ToastData';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {ErrorToast} from '@mintdata-wsp/wsp/ui/components/toasts/ErrorToast';
import {FileUploadToast} from '@mintdata-wsp/wsp/ui/components/toasts/FileUploadToast';
import {SlowConnectionToast} from '@mintdata-wsp/wsp/ui/components/toasts/SlowConnectionToast';
import {SocketReconnectionToast} from '@mintdata-wsp/wsp/ui/components/toasts/SocketReconnectionToast';
import {Assert} from '@mintdata/mint-kit/impl/util/Assert';
import {action, runInAction} from 'mobx';
import * as React from 'react';

export class ToastController
{
    @action.bound
    public showToast(toast:ToastData):void
    {
        rootState.ui.visualDesigner.toastsState.toasts.set(toast.id, toast);

        if( toast.isAutoHide )
            this.autoHideToast(toast.id, toast.hideDelay);
    }

    @action.bound
    public hideToast(toastId:ToastId):void
    {
        const toast:ToastData = rootState.ui.visualDesigner.toastsState.toasts.get(toastId);

        Assert.yes(toast, 'Toast already has been hidden');

        rootState.ui.visualDesigner.toastsState.toasts.delete(toast.id);
    }

    public autoHideToast(toastId:ToastId, delay:number):void
    {
        setTimeout(() =>
        {
            runInAction(() =>
            {
                const toast:ToastData = rootState.ui.visualDesigner.toastsState.toasts.get(toastId);

                if( toast )
                {
                    rootState.ui.visualDesigner.toastsState.toasts.delete(toast.id);
                }
            });
        }, delay);
    }

    @action.bound
    public showError(error):void
    {
        const errorMessage:string = error.message || error.toString();
        const errorSubMessage:string = error.subMessage !== null && typeof error.subMessage !== 'undefined' ? error.subMessage : '';
        const existingToast:ToastData = Array.from(rootState.ui.visualDesigner.toastsState.toasts.values()).find(
            (toast:ToastData) => toast instanceof ErrorToastData && toast.message === errorMessage
        );
        let toast:ErrorToastData;

        if( existingToast )
        {
            toast = existingToast as ErrorToastData;
            toast.count++;

            this.hideToast(toast.id);
        }
        else
        {
            toast = new ErrorToastData(
                errorMessage,
                errorSubMessage,
                error.stack,
                (toast:ErrorToastData) => <ErrorToast toast={toast} onClose={this.hideToast} />,
                false
            );
        }

        this.showToast(toast);
    }

    // @action.bound
    // public showImportTabsToast(importInstance:ImportInstance):void
    // {
    //     const toast = new ImportTabsToastData(
    //         importInstance,
    //         (toast:ImportTabsToastData) => (
    //             <ImportTabsToast
    //                 toast={toast}
    //                 onClose={() =>
    //                 {
    //                     this.hideToast(toast.id);
    //                     controllers.ui.spreadsheets.importTabs.disposeImport(toast.importInstance.id);
    //                 }}
    //                 onCancelUpload={() =>
    //                 {
    //                     this.hideToast(toast.id);
    //                     controllers.ui.spreadsheets.importTabs.cancelUploading(toast.importInstance.id);
    //                 }}
    //                 onCancelImport={() =>
    //                 {
    //                     this.hideToast(toast.id);
    //                     controllers.ui.spreadsheets.importTabs.cancelImporting(toast.importInstance.id);
    //                 }}
    //             />
    //         )
    //     );
    //
    //     this.showToast(toast);
    // }

    // @action.bound
    // public hideImportTabsToast(id:ImportId):void
    // {
    //     const allToasts = rootState.ui.visualDesigner.toastsState.toasts;
    //
    //     const toastToHide = Array.from(allToasts.values()).find((toast:ImportTabsToastData) => (
    //         toast.importInstance.id === id
    //     ));
    //
    //     if( toastToHide )
    //         this.hideToast(toastToHide.id);
    // }

    @action.bound
    public showSocketReconnection(networkState:NetworkState):void
    {
        this.hideSocketReconnection();

        const toast = new SocketReconnectionToastData(
            networkState,
            (toast:SocketReconnectionToastData) => <SocketReconnectionToast
                toast={toast}
                onClose={this.hideToast}
                onAutoHide={this.onAutoHideToast}
            />
        );

        this.showToast(toast);
    }

    @action.bound
    public hideSocketReconnection():void
    {
        const allToasts = rootState.ui.visualDesigner.toastsState.toasts;

        const toastToHide:ToastData = Array.from(allToasts.values()).find(
            (toast:ToastData) => toast instanceof SocketReconnectionToastData
        );

        if( toastToHide )
            this.hideToast(toastToHide.id);
    }

    @action.bound
    public showFileUploadToast(uploadInstance:UploadInstance):void
    {
        const toast = new FileUploadToastData(
            uploadInstance,
            (toast:FileUploadToastData) => (
                <FileUploadToast
                    toast={toast}
                    onClose={() =>
                    {
                        this.hideToast(toast.id);
                        controllers.ui.surfaceFileUpload.disposeUpload(toast.uploadInstance.id);
                    }}
                    onCancel={() =>
                    {
                        this.hideToast(toast.id);
                        controllers.ui.surfaceFileUpload.cancelUploading(toast.uploadInstance.id);
                    }}
                    onAutoHide={() =>
                    {
                        this.autoHideToast(toast.id, 5000);
                        controllers.ui.surfaceFileUpload.disposeUpload(toast.uploadInstance.id);
                    }}
                />
            )
        );

        this.showToast(toast);
    }

    showSlowConnection(networkState: NetworkState) {
        const toast = new SlowConnectionToastData(
            networkState,
            (toast:SlowConnectionToastData) => <SlowConnectionToast
                toast={toast}
                onClose={this.hideToast}
                onAutoHide={this.onAutoHideToast}
            />
        );

        const existingToast:ToastData = Array.from(rootState.ui.visualDesigner.toastsState.toasts.values()).find(
            (toast:ToastData) => toast instanceof SlowConnectionToastData
        );

        if( existingToast )
            this.hideToast(existingToast.id);

        this.showToast(toast);
    }
    
    @action.bound
    public hideFileUploadToast(id:ImportId):void
    {
        const allToasts = rootState.ui.visualDesigner.toastsState.toasts;

        const toastToHide = Array.from(allToasts.values()).find((toast:FileUploadToastData) => (
            toast.uploadInstance.id === id
        ));

        if( toastToHide )
            this.hideToast(toastToHide.id);
    }

    @action.bound
    public onAutoHideToast(toastId:ToastId):void
    {
        this.autoHideToast(toastId, 5000);
    }
}

